import React, { memo, useEffect, useMemo, useState } from 'react';
import { Grid, IconButton, Tooltip, TextField, FormControlLabel, Checkbox } from '@material-ui/core';
import { css } from '@emotion/react';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { useItemDynamicFields } from '@/components/ShipmentForm/hooks/items';
import {
    ItemType,
    ItemTypeDetails,
    BedSize,
    AssemblyType,
    ItemIsPackagedSelect,
    ItemPieceCountSelect,
    OnwardTextField,
    Disposition,
} from '../../../InputFields';
import { TabSection, TabRow } from '../../../../blocks';
import { ITEM_LABELS_BY_FREIGHT } from '@/components/ShipmentForm/constants/freightTypes';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { colors } from '@/styles';
import { useClientUser } from '@/hooks';
import { getItemErrors } from './utils';
import {
    dirtyDimensionKeys,
    skuCatalogPatch,
    skuDimensionOverrides,
} from '@/components/ShipmentForm/utilities/skuHydration';

// MUI v4's Autocomplete has no virtualization, so cap the rendered popup. Search still scans
// every option — createFilterOptions applies its limit after filtering.
const POPUP_LIMIT = 100;
const filterBySearch = createFilterOptions({ limit: POPUP_LIMIT });
const identity = (option) => option;

/**
 * One item row in the Items tab of the Edit Order modal: SKU, type, dimensions, assembly,
 * disposition, and copy and delete actions. Derives its own errors with `getItemErrors`.
 *
 * @param {object} props
 * @param {object[]} props.skus Shipper's SKU catalog for the SKU picker.
 * @param {object} props.item The item being edited.
 * @param {number} props.index Position in `order.itemsByOrderId`.
 * @param {string} props.orderType Order `order_type`.
 * @param {string} props.freightType Order `freight_type`.
 * @param {string} props.shipperId Order `shipper_id`, for catalog lookups.
 * @param {boolean} props.oms Order `oms`.
 * @param {string} props.sourceForm Order `source_form`.
 * @param {boolean} props.assemblyRequired Order `assembly_required`.
 * @param {boolean} props.isFirstReturnItem Shows the apply-disposition-to-all control on this row.
 * @param {Function} props.editItem `(index, changes)` patches the item.
 * @param {Function} props.makeItemDirty `(index, fields)` marks fields touched.
 * @param {Function} props.copyItem Adds a copy of the item.
 * @param {Function} props.deleteItem `(index)` removes the item.
 * @param {Function} props.editPallet `(index, palletId, changes)` patches the item's pallet.
 * @param {Function} props.applyDispositionToReturns `(disposition, index)` sets it on every return item.
 * @param {object} props.isDirty This item's touched fields.
 * @param {boolean} props.startDirty Show errors before fields are touched.
 * @param {boolean} props.applyDispositionToAll Tab-level apply-to-all checkbox state.
 * @param {Function} props.setApplyDispositionToAll Setter for `applyDispositionToAll`.
 * @param {string} props.dispositionToApply Disposition fanned out to return items.
 * @param {Function} props.setDispositionToApply Setter for `dispositionToApply`.
 */
const ItemDetails = ({
    skus,
    item,
    index,
    orderType,
    freightType,
    shipperId,
    oms,
    sourceForm,
    assemblyRequired,
    isFirstReturnItem,
    editItem,
    makeItemDirty,
    copyItem,
    deleteItem,
    editPallet,
    applyDispositionToReturns,
    isDirty,
    startDirty,
    applyDispositionToAll,
    setApplyDispositionToAll,
    dispositionToApply,
    setDispositionToApply,
}) => {
    const [dimensionOverrides, setDimensions] = useState({
        length: item.length,
        width: item.width,
        height: item.height,
        weight: item.weight,
        cubes: item.cubes_per_unit,
    });
    const [serviceTimeTouched, setServiceTimeTouched] = useState(Number.isFinite(item.service_time));
    // Held until the picked SKU's catalog row resolves.
    const [pendingSkuPick, setPendingSkuPick] = useState(null);
    // Untouched => browse the whole catalog, typed => filter. Can't be derived from inputValue;
    // see filterOptions below.
    const [skuTyped, setSkuTyped] = useState(false);
    const { catalogRow } = useItemDynamicFields(
        item,
        (changes) => {
            editItem(index, changes);
        },
        shipperId,
        dimensionOverrides,
        serviceTimeTouched
    );

    // A pick overwrites rather than gap-fills. Must stay below useItemDynamicFields so this
    // patch lands on top of that hook's in the same commit.
    useEffect(() => {
        if (!pendingSkuPick) return;
        // Typed past the pick before its row landed — abandon it.
        if (item.sku !== pendingSkuPick) {
            setPendingSkuPick(null);
            return;
        }
        if (catalogRow?.sku !== pendingSkuPick) return;

        setDimensions((prev) => skuDimensionOverrides(prev, catalogRow));
        const patch = skuCatalogPatch(item, catalogRow, { overwrite: true });
        if (Object.keys(patch).length > 0) {
            editItem(index, patch);
        }
        makeItemDirty(index, [...dirtyDimensionKeys(catalogRow), ...Object.keys(patch)]);
        setPendingSkuPick(null);
    }, [pendingSkuPick, catalogRow, item.sku]);
    const { circles, special_instructions_settings } = useClientUser();
    const specialInstructions = useMemo(() => {
        if (orderType === 'wh_return') {
            return special_instructions_settings?.['Warehouse Return'] || '';
        } else {
            return special_instructions_settings?.['Return'] || '';
        }
    }, [orderType, special_instructions_settings]);

    const hasError = useMemo(
        () => getItemErrors(item, { oms, freightType, sourceForm, assemblyRequired }),
        [item, oms, freightType, sourceForm, assemblyRequired]
    );

    const isPallet = freightType === 'pallet';
    const isContainer = freightType === 'containers';
    const isBedItem = useMemo(() => {
        if (item?.item_type_details) {
            return ['mattress', 'boxSpring', 'bedFrame'].includes(item.item_type_details);
        }

        return false;
    }, [item]);

    const isWillCall = orderType === 'will_call';
    // Inventory orders must contain only inventory lines, so items can't be switched to return.
    const isInventory = orderType === 'wh_inventory';

    const handleItemTypeChange = (e) => {
        const isChangingToReturn = e.target.value === 'return';
        editItem(index, {
            is_return: isChangingToReturn,
            ...(isChangingToReturn
                ? {
                      special_instructions: specialInstructions,
                      disposition: applyDispositionToAll ? dispositionToApply : 'UNKNOWN',
                  }
                : { special_instructions: '' }),
        });
    };

    const preAllocatedNotice = item.forward_stocking_source_item_id ? (
        <Grid item xs={12}>
            <span
                css={css`
                    font-size: 13px;
                    color: ${colors.greys.subtitle};
                `}
            >
                *Pre-allocated from inventory, serial number/line id will be populated on final order submission
            </span>
        </Grid>
    ) : null;

    let config = (
        <>
            <TabRow>
                <Grid item xs={2}>
                    <ItemType
                        disabled={isWillCall || isInventory}
                        orderType={orderType}
                        value={item.is_return ? 'return' : 'delivery'}
                        onChange={(e) => handleItemTypeChange(e)}
                    />
                </Grid>
                <Grid item xs={2}>
                    <OnwardTextField
                        variant="outlined"
                        fullWidth
                        label="Item Class"
                        value={item.class || ''}
                        onChange={(e) => {
                            editItem(index, { class: e.target.value });
                            makeItemDirty(index, ['class']);
                        }}
                        error={hasError.class && (startDirty || isDirty.class)}
                    />
                </Grid>
                <Grid item xs={2}>
                    <OnwardTextField
                        variant="outlined"
                        fullWidth
                        label="NFMC #"
                        value={item.nfmc_no || ''}
                        onChange={(e) => {
                            editItem(index, { nfmc_no: e.target.value });
                            makeItemDirty(index, ['nfmc_no']);
                        }}
                        error={hasError.nfmc_no && (startDirty || isDirty.nfmc_no)}
                    />
                </Grid>
                <Grid item xs={2}>
                    <OnwardTextField
                        variant="outlined"
                        fullWidth
                        label="Barcode ID"
                        value={item.barcode || ''}
                        onChange={(e) => {
                            editItem(index, { barcode: e.target.value });
                            makeItemDirty(index, ['barcode']);
                        }}
                        error={hasError.barcode && (startDirty || isDirty.barcode)}
                    />
                </Grid>
                <Grid item xs={2}>
                    <OnwardTextField
                        variant="outlined"
                        fullWidth
                        label="Line ID"
                        value={item.line_id || ''}
                        onChange={(e) => {
                            editItem(index, { line_id: e.target.value.trim() || null });
                            makeItemDirty(index, ['line_id']);
                        }}
                        error={hasError.line_id && (startDirty || isDirty.line_id)}
                    />
                </Grid>
                <Grid item xs={2}>
                    <OnwardTextField
                        variant="outlined"
                        fullWidth
                        label="Serial Number"
                        // Scans must reach the order before Done reads it.
                        debounced={false}
                        value={item.serial_number || ''}
                        onChange={(e) => {
                            editItem(index, { serial_number: e.target.value });
                            makeItemDirty(index, ['serial_number']);
                        }}
                        error={hasError.serial_number && (startDirty || isDirty.serial_number)}
                    />
                </Grid>
                {preAllocatedNotice}
            </TabRow>
        </>
    );
    if (!isPallet && !isContainer) {
        config = (
            <>
                <TabRow>
                    <Grid item xs={4}>
                        <ItemType
                            disabled={isWillCall || isInventory}
                            orderType={orderType}
                            value={item.is_return ? 'return' : 'delivery'}
                            onChange={(e) => handleItemTypeChange(e)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <ItemTypeDetails
                            required={!oms}
                            value={item.item_type_details}
                            freightType={freightType}
                            onChange={(e) => {
                                editItem(index, { item_type_details: e.target.value });
                                const toDirty = ['item_type_details'];
                                if (e.target.value !== 'mattress') {
                                    // push, not concat — concat's result was discarded, so these
                                    // were never marked dirty and their errors stayed hidden.
                                    toDirty.push('length', 'width', 'height', 'weight', 'cubes_per_unit');
                                }
                                makeItemDirty(index, toDirty);
                            }}
                            error={hasError.item_type_details && (startDirty || isDirty.item_type_details)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <BedSize
                            disabled={!isBedItem}
                            value={{ height: item.height, width: item.width, length: item.length }}
                            itemType={item.item_type_details}
                            onChange={({ height, length, weight, width }) => {
                                setDimensions((prev) => ({
                                    ...prev,
                                    height,
                                    length,
                                    weight,
                                    width,
                                }));
                                makeItemDirty(index, ['length', 'width', 'height', 'weight', 'cubes_per_unit']);
                            }}
                            error={
                                isBedItem &&
                                ['length', 'width', 'height'].some(
                                    (attr) => hasError[attr] && (startDirty || isDirty[attr])
                                )
                            }
                            onWheel={(event) => event.target.blur()}
                        />
                    </Grid>
                </TabRow>

                {item.is_return && (
                    <>
                        <TabRow>
                            <Grid item xs={isFirstReturnItem ? 6 : 12}>
                                <Disposition
                                    required
                                    value={item.disposition || 'UNKNOWN'}
                                    onChange={(e) => {
                                        const newDisposition = e.target.value;
                                        editItem(index, { disposition: newDisposition });
                                        if (isFirstReturnItem) {
                                            setDispositionToApply(newDisposition);
                                        }
                                        if (applyDispositionToAll && isFirstReturnItem) {
                                            applyDispositionToReturns(newDisposition, index);
                                        }
                                    }}
                                />
                            </Grid>
                            {isFirstReturnItem && (
                                <Grid item xs={6}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={applyDispositionToAll}
                                                onChange={(e) => {
                                                    setApplyDispositionToAll(e.target.checked);
                                                    if (e.target.checked && isFirstReturnItem) {
                                                        // UNKNOWN mirrors the select's own fallback.
                                                        const fanOut = item.disposition || 'UNKNOWN';
                                                        // Seed this too, or a disposition loaded
                                                        // with the order stays UNKNOWN for items
                                                        // switched to return later.
                                                        setDispositionToApply(fanOut);
                                                        applyDispositionToReturns(fanOut, index);
                                                    }
                                                }}
                                            />
                                        }
                                        label="Apply same disposition to all items in order"
                                    />
                                </Grid>
                            )}
                        </TabRow>
                    </>
                )}

                <TabRow>
                    <Grid item xs={2}>
                        <OnwardTextField
                            variant="outlined"
                            color="primary"
                            label="Pallet ID"
                            value={item.pallet?.data?.pallet_name || item.pallet?.pallet_name}
                            onChange={(e) => editPallet(index, item.pallet_id, { pallet_name: e.target.value || null })}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <OnwardTextField
                            variant="outlined"
                            fullWidth
                            label="Item Class"
                            value={item.class}
                            onChange={(e) => {
                                editItem(index, { class: e.target.value });
                                makeItemDirty(index, ['class']);
                            }}
                            error={hasError.class && (startDirty || isDirty.class)}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <OnwardTextField
                            variant="outlined"
                            fullWidth
                            label="NFMC #"
                            value={item.nfmc_no}
                            onChange={(e) => {
                                editItem(index, { nfmc_no: e.target.value });
                                makeItemDirty(index, ['nfmc_no']);
                            }}
                            error={hasError.nfmc_no && (startDirty || isDirty.nfmc_no)}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <OnwardTextField
                            variant="outlined"
                            fullWidth
                            label="Barcode ID"
                            value={item.barcode}
                            onChange={(e) => {
                                editItem(index, { barcode: e.target.value });
                                makeItemDirty(index, ['barcode']);
                            }}
                            error={hasError.barcode && (startDirty || isDirty.barcode)}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <OnwardTextField
                            variant="outlined"
                            fullWidth
                            label="Line ID"
                            value={item.line_id || ''}
                            onChange={(e) => {
                                editItem(index, { line_id: e.target.value.trim() || null });
                                makeItemDirty(index, ['line_id']);
                            }}
                            error={hasError.line_id && (startDirty || isDirty.line_id)}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <OnwardTextField
                            variant="outlined"
                            fullWidth
                            label="Serial Number"
                            // Scans must reach the order before Done reads it.
                            debounced={false}
                            value={item.serial_number}
                            onChange={(e) => {
                                editItem(index, { serial_number: e.target.value });
                                makeItemDirty(index, ['serial_number']);
                            }}
                            error={hasError.serial_number && (startDirty || isDirty.serial_number)}
                        />
                    </Grid>
                    {preAllocatedNotice}
                </TabRow>

                <TabRow>
                    <Grid item xs={3}>
                        <AssemblyType
                            required={assemblyRequired || !oms}
                            value={item.assembly_type}
                            disabled={!!circles?.['import-lock-wg-light-assembly']}
                            onChange={(e) => {
                                editItem(index, { assembly_type: e.target.value });
                                makeItemDirty(index, ['assembly_type']);
                            }}
                            error={hasError.assembly_type && (startDirty || isDirty.assembly_type)}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <OnwardTextField
                            label="Service Time (min)"
                            variant="outlined"
                            type="number"
                            fullWidth
                            value={Number.isFinite(item.service_time) ? item.service_time : ''}
                            onChange={(e) => {
                                const parsed = parseInt(e.target.value, 10);
                                const newVal = Number.isFinite(parsed) ? parsed : null;
                                setServiceTimeTouched(newVal !== null);
                                editItem(index, { service_time: newVal });
                            }}
                            onWheel={(event) => event.target.blur()}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <ItemIsPackagedSelect
                            required={!oms}
                            value={item.is_packaged}
                            onChange={(e) => {
                                editItem(index, { is_packaged: e.target.value });
                            }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <ItemPieceCountSelect
                            required={!oms || circles?.['box-piece-count']}
                            value={item.item_piece_count}
                            onChange={(e) => {
                                editItem(index, { item_piece_count: e.target.value });
                                makeItemDirty(index, ['item_piece_count']);
                            }}
                            error={hasError.item_piece_count && (startDirty || isDirty.item_piece_count)}
                        />
                    </Grid>
                </TabRow>
            </>
        );
    }

    return (
        <>
            <TabRow
                css={css`
                    align-items: center;
                    margin-bottom: 12px;
                    justify-content: space-between;
                `}
            >
                <Grid
                    css={css`
                        display: flex;
                        align-items: center;
                        height: 100%;
                    `}
                    item
                >
                    <TabSection
                        css={css`
                            display: flex;
                            align-items: flex-end;
                            height: 100%;
                        `}
                    >{`${ITEM_LABELS_BY_FREIGHT[freightType] || 'Item'} ${index + 1}`}</TabSection>
                </Grid>
                <Grid item>
                    <Grid container spacing={1}>
                        <Grid item>
                            <Tooltip title="Duplicate Item">
                                <IconButton
                                    onClick={() => copyItem({ ...item })}
                                    css={css`
                                        color: ${colors.greens.primary};
                                        border-radius: 4px;
                                        padding: 6px;
                                    `}
                                >
                                    <ContentCopyOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                        <Grid item>
                            <Tooltip title="Delete">
                                <IconButton
                                    onClick={() => deleteItem(index)}
                                    css={css`
                                        color: ${colors.reds.primary};
                                        border-radius: 4px;
                                        padding: 6px;
                                    `}
                                >
                                    <DeleteOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Grid>
            </TabRow>

            {config}

            <TabRow>
                <Grid item xs={2}>
                    <OnwardTextField
                        type="number"
                        variant="outlined"
                        label="Quantity"
                        value={item.quantity}
                        fullWidth
                        InputProps={{ inputProps: { min: 1 } }}
                        onChange={(e) => {
                            editItem(index, {
                                quantity: Number(e.target.value),
                            });
                        }}
                    />
                </Grid>
                <Grid item xs={2}>
                    <OnwardTextField
                        label="Weight (lb)"
                        variant="outlined"
                        fullWidth
                        value={item.weight || ''}
                        error={hasError.weight && (startDirty || isDirty.weight)}
                        onBlur={() => makeItemDirty(index, ['weight'])}
                        onChange={(e) => {
                            const str = `${e.target.value}`.replaceAll(/,|\s/g, '');
                            setDimensions((prev) => ({ ...prev, weight: parseInt(str, 10) || 0 }));
                        }}
                    />
                </Grid>
                <Grid item xs={2}>
                    <OnwardTextField
                        label="Length (in)"
                        variant="outlined"
                        fullWidth
                        value={item.length || ''}
                        error={hasError.length && (startDirty || isDirty.length)}
                        onBlur={() => makeItemDirty(index, ['length'])}
                        onChange={(e) =>
                            setDimensions((prev) => ({ ...prev, length: parseInt(e.target.value, 10) || 0 }))
                        }
                    />
                </Grid>
                <Grid item xs={2}>
                    <OnwardTextField
                        label="Width (in)"
                        variant="outlined"
                        fullWidth
                        value={item.width || ''}
                        error={hasError.width && (startDirty || isDirty.width)}
                        onBlur={() => makeItemDirty(index, ['width'])}
                        onChange={(e) =>
                            setDimensions((prev) => ({ ...prev, width: parseInt(e.target.value, 10) || 0 }))
                        }
                    />
                </Grid>
                <Grid item xs={2}>
                    <OnwardTextField
                        label="Height (in)"
                        variant="outlined"
                        fullWidth
                        value={item.height || ''}
                        error={hasError.height && (startDirty || isDirty.height)}
                        onBlur={() => makeItemDirty(index, ['height'])}
                        onChange={(e) =>
                            setDimensions((prev) => ({ ...prev, height: parseInt(e.target.value, 10) || 0 }))
                        }
                    />
                </Grid>
                <Grid item xs={2}>
                    <OnwardTextField
                        label="Cubic Feet"
                        variant="outlined"
                        fullWidth
                        value={item.cubes_per_unit || ''}
                        error={hasError.cubes_per_unit && (startDirty || isDirty.cubes_per_unit)}
                        onBlur={() => makeItemDirty(index, ['cubes_per_unit'])}
                        onChange={(e) =>
                            setDimensions((prev) => ({ ...prev, cubes: parseInt(e.target.value, 10) || 0 }))
                        }
                    />
                </Grid>
            </TabRow>
            <TabRow>
                <Grid item xs={2}>
                    <Autocomplete
                        freeSolo
                        disableClearable
                        fullWidth
                        value={item.sku || ''}
                        inputValue={item.sku || ''}
                        options={skus || []}
                        noOptionsText="Enter new SKU"
                        filterOptions={(options) =>
                            // useAutocomplete passes inputValue: '' whenever inputValue ===
                            // getOptionLabel(value), and this input controls both to item.sku,
                            // so the search term has to be fed in by hand.
                            skuTyped
                                ? filterBySearch(options, {
                                      inputValue: item.sku || '',
                                      getOptionLabel: identity,
                                  })
                                : options.slice(0, POPUP_LIMIT)
                        }
                        onClose={(_, reason) => {
                            setSkuTyped(false);
                            // useAutocomplete skips onChange when the picked option already
                            // equals value — i.e. the user typed the SKU out in full before
                            // selecting it. Close still reports the pick.
                            if (reason === 'select-option') {
                                setPendingSkuPick((pending) => pending || item.sku || null);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                color="primary"
                                variant="outlined"
                                label="SKU"
                                onFocus={() => setSkuTyped(false)}
                                onBlur={() => makeItemDirty(index, ['sku'])}
                                onKeyDown={(e) => {
                                    if (['Backspace'].includes(e.key)) {
                                        e.stopPropagation();
                                    }
                                }}
                            />
                        )}
                        onChange={(_, value) => {
                            const nextSku = typeof value === 'string' ? value : '';
                            editItem(index, { sku: nextSku });
                            makeItemDirty(index, ['sku']);
                            setPendingSkuPick(nextSku || null);
                        }}
                        onInputChange={(_, newInputValue, reason) => {
                            if (reason !== 'input' && reason !== 'clear') return;
                            if (reason === 'input') setSkuTyped(true);
                            if ((item.sku || '') === (newInputValue || '')) return;
                            editItem(index, { sku: newInputValue || '' });
                        }}
                    />
                </Grid>
                <Grid item xs={10}>
                    <OnwardTextField
                        label={isPallet ? 'Description of palletized item' : 'Description'}
                        variant="outlined"
                        name="desc"
                        fullWidth
                        value={item.description}
                        onChange={(e) => editItem(index, { description: e.target.value })}
                    />
                </Grid>
            </TabRow>
            {item.is_return && (
                <TabRow>
                    <Grid item xs={12}>
                        <OnwardTextField
                            label="Special Instructions"
                            variant="outlined"
                            fullWidth
                            value={item.special_instructions}
                            onChange={(e) => editItem(index, { special_instructions: e.target.value })}
                        />
                    </Grid>
                </TabRow>
            )}
        </>
    );
};
// An order can carry hundreds of items and every edit replaces the order clone, so the row's props
// are deliberately scalars plus its own item — that keeps the default shallow compare effective.
export default memo(ItemDetails);
